import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './FallingPetalsCSS.css';
import { recolorFlat, recolorGradient } from './recolor';
import lotusSilhouette from '@shared/assets/falling-petals/lotus-silhouette.png';
import lotusGradient from '@shared/assets/falling-petals/lotus-gradient.png';
import lotusMandala from '@shared/assets/falling-petals/lotus-mandala.png';

// 三款成熟蓮花素材（CC0 公共領域授權，取自 freesvg.org）：
// silhouette - 側視剪影，單色可自訂任意顏色
// gradient   - 側視漸層彩色（已去除莖梗），保留明暗層次、可依主色重新調色
// mandala    - 俯視放射對稱圖騰，線條風格，單色可自訂
const VARIANTS = {
    silhouette: { src: lotusSilhouette, recolorMode: 'flat' },
    gradient: { src: lotusGradient, recolorMode: 'gradient' },
    mandala: { src: lotusMandala, recolorMode: 'flat' },
};

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

/**
 * 飄落整朵蓮花特效 - 使用成熟美術素材（非程式繪製）
 * Falling whole lotus flowers - built on curated art assets rather than hand-coded shapes
 *
 * 素材取自 freesvg.org（CC0 公共領域授權），三款可選：側視剪影／側視漸層／俯視圖騰。
 * 飄落時下墜＋左右搖擺，同時持續翻轉，像真實花瓣打轉飄落。
 */
const FallingPetalsImage = ({
    variant = 'silhouette',
    count = 18,
    color = '#F5C97B',
    minSize = 36,
    maxSize = 72,
    minDuration = 8,
    maxDuration = 16,
    className = '',
}) => {
    const config = VARIANTS[variant] ?? VARIANTS.silhouette;
    const [resolvedSrc, setResolvedSrc] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const recolorFn = config.recolorMode === 'gradient' ? recolorGradient : recolorFlat;
        recolorFn(config.src, color).then((canvas) => {
            if (!cancelled) setResolvedSrc(canvas.toDataURL('image/png'));
        });
        return () => { cancelled = true; };
    }, [config.src, config.recolorMode, color]);

    const petals = useMemo(() => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            left: randomBetween(0, 100),
            size: randomBetween(minSize, maxSize),
            duration: randomBetween(minDuration, maxDuration),
            delay: randomBetween(0, maxDuration),
            swayDuration: randomBetween(2.5, 5),
            rotateDuration: randomBetween(4, 9),
            spinDirection: Math.random() > 0.5 ? 1 : -1,
            opacity: randomBetween(0.88, 1),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, minSize, maxSize, minDuration, maxDuration]);

    if (!resolvedSrc) return null;

    return (
        <div className={`falling-petals-css ${className}`} aria-hidden="true">
            {petals.map((p) => (
                <div
                    key={p.id}
                    className="falling-petal-fall"
                    style={{
                        left: `${p.left}%`,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${-p.delay}s`,
                    }}
                >
                    <div
                        className="falling-petal-sway"
                        style={{
                            animationDuration: `${p.swayDuration}s`,
                            animationDelay: `${-p.delay}s`,
                        }}
                    >
                        <div
                            className="falling-petal-spin"
                            style={{
                                animationDuration: `${p.rotateDuration}s`,
                                animationDirection: p.spinDirection === 1 ? 'normal' : 'reverse',
                                animationDelay: `${-p.delay}s`,
                            }}
                        >
                            <img
                                src={resolvedSrc}
                                alt=""
                                width={p.size}
                                height={p.size}
                                style={{
                                    opacity: p.opacity,
                                    display: 'block',
                                    filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.18))',
                                }}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

FallingPetalsImage.propTypes = {
    variant: PropTypes.oneOf(['silhouette', 'gradient', 'mandala']),
    count: PropTypes.number,
    color: PropTypes.string,
    minSize: PropTypes.number,
    maxSize: PropTypes.number,
    minDuration: PropTypes.number,
    maxDuration: PropTypes.number,
    className: PropTypes.string,
};

export default FallingPetalsImage;
